import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import { Ship, Plus, Pencil, Trash2, Loader2, ArrowLeft, FolderOpen, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// D2 FIX: Helper para obter headers de autenticação
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EquipmentGroupsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    equipment_ids: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // D2 FIX: Adicionar auth headers
      const [groupsRes, eqRes] = await Promise.all([
        axios.get(`${API}/rbac/equipment-groups`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/equipments`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setGroups(groupsRes.data || []);
      setEquipments(eqRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name,
        code: group.code || '',
        description: group.description || '',
        equipment_ids: group.equipment_ids || [],
      });
    } else {
      setEditingGroup(null);
      setFormData({ name: '', code: '', description: '', equipment_ids: [] });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }

    setSaving(true);
    try {
      // D2 FIX: Adicionar auth headers
      if (editingGroup) {
        await axios.put(`${API}/rbac/equipment-groups/${editingGroup.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Grupo atualizado');
      } else {
        await axios.post(`${API}/rbac/equipment-groups`, formData, { headers: getAuthHeaders() });
        toast.success('Grupo criado');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving group:', error);
      // D2 FIX: Melhorar error handling para não crashar
      const detail = error.response?.data?.detail || 'Erro ao salvar grupo';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!confirm('Deseja realmente excluir este grupo?')) return;

    try {
      // D2 FIX: Adicionar auth headers
      await axios.delete(`${API}/rbac/equipment-groups/${groupId}`, { headers: getAuthHeaders() });
      toast.success('Grupo excluído');
      fetchData();
    } catch (error) {
      console.error('Error deleting group:', error);
      const detail = error.response?.data?.detail || 'Erro ao excluir grupo';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const toggleEquipment = (equipmentId) => {
    setFormData(prev => ({
      ...prev,
      equipment_ids: prev.equipment_ids.includes(equipmentId)
        ? prev.equipment_ids.filter(id => id !== equipmentId)
        : [...prev.equipment_ids, equipmentId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="equipment-groups-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/equipments')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Equipamentos
          </Button>
          <Separator orientation="vertical" className="h-8" />
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FolderOpen className="w-6 h-6 text-primary" />
              Grupos de Equipamentos
            </h1>
            <p className="text-muted-foreground text-sm">
              Agrupe equipamentos para controle de acesso e visualização
            </p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-group-btn">
          <Plus className="w-4 h-4" />
          Novo Grupo
        </Button>
      </div>

      {/* Groups Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => {
          const groupEquipments = equipments.filter(eq => group.equipment_ids?.includes(eq.id));

          return (
            <Card key={group.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Ship className="w-5 h-5 text-blue-500" />
                      {group.name}
                    </CardTitle>
                    {group.code && (
                      <Badge variant="outline" className="mt-1 font-mono text-xs">
                        {group.code}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(group)}
                      data-testid={`edit-group-${group.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(group.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-group-${group.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {group.description && (
                  <p className="text-sm text-muted-foreground mb-3">{group.description}</p>
                )}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    {groupEquipments.length} equipamento(s):
                  </p>
                  {groupEquipments.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {groupEquipments.slice(0, 5).map((eq) => (
                        <Badge key={eq.id} variant="secondary" className="text-xs">
                          {eq.name}
                        </Badge>
                      ))}
                      {groupEquipments.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{groupEquipments.length - 5}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Nenhum equipamento</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {groups.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-10 text-center">
              <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-30" />
              <p className="text-muted-foreground">Nenhum grupo de equipamentos cadastrado</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleOpenDialog()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Grupo
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Editar Grupo' : 'Novo Grupo de Equipamentos'}</DialogTitle>
            <DialogDescription>
              {editingGroup
                ? 'Atualize as informações e equipamentos do grupo'
                : 'Crie um grupo para organizar equipamentos'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Nome do Grupo *</Label>
              <Input
                id="group-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Rebocadores Porto de Itajaí"
                data-testid="group-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-code">Código (opcional)</Label>
              <Input
                id="group-code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: REB-ITJ"
                maxLength={10}
                disabled={!!editingGroup}
                data-testid="group-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-desc">Descrição</Label>
              <Input
                id="group-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do grupo"
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Equipamentos no Grupo</Label>
              <p className="text-sm text-muted-foreground">
                Selecione os equipamentos que farão parte deste grupo
              </p>
              <div className="border rounded-md p-3 space-y-2 max-h-[200px] overflow-y-auto">
                {equipments.length > 0 ? (
                  equipments.map((eq) => (
                    <div key={eq.id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        id={`eq-${eq.id}`}
                        checked={formData.equipment_ids.includes(eq.id)}
                        onCheckedChange={() => toggleEquipment(eq.id)}
                      />
                      <label htmlFor={`eq-${eq.id}`} className="flex-1 cursor-pointer">
                        <span className="font-medium">{eq.name}</span>
                        {eq.tag && <Badge variant="outline" className="ml-2 text-xs">{eq.tag}</Badge>}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum equipamento cadastrado
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {formData.equipment_ids.length} equipamento(s) selecionado(s)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-group-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentGroupsPage;
